import { HomeyAPI, type SystemStorageInfo } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { formatBytes } from '../../../lib/formatBytes';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type StorageInfoItem = SystemStorageInfo['types'][string];

interface State {
  storageInfoData: SystemStorageInfo | null;
  free: number;
  freeFormatted: string;
  total: number;
  totalFormatted: string;
  items: (StorageInfoItem & { key: string; sizeFormatted: string; color: string })[];
  isLoading: boolean;
  error: unknown;
}

type GeneralSettingsInfoStorageStoreState = State;

function getColorForIndex(index: number) {
  let hue = index * 50;
  hue += 150;
  hue %= 360;

  return `hsl(${hue}, 80%, 70%)`;
}

export class GeneralSettingsInfoStorageStore extends BaseApiStore<State> {
  constructor() {
    super('general-settings-info-storage');
  }

  override createInitialState() {
    return {
      storageInfoData: null,
      free: 0,
      freeFormatted: '',
      total: 0,
      totalFormatted: '',
      items: [],
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      this.set({ ...this.createInitialState(), isLoading: false });
      return;
    }

    try {
      // Apply 30s timeout instead of 10s as this can take longer on busy Homeys
      const storageInfoData = await api.system.getStorageInfo({
        $timeout: 30000,
      });

      if (abortController.signal.aborted) {
        return;
      }

      const free = storageInfoData.free ?? 0;
      const total = storageInfoData.total ?? 0;

      const items = Object.entries(storageInfoData.types ?? {})
        .sort(([, left], [, right]) => {
          return right.size - left.size;
        })
        .map(([key, value], index) => {
          return {
            ...value,
            key,
            sizeFormatted: formatBytes(value.size),
            color: getColorForIndex(index),
          };
        });

      this.set({
        ...this.createInitialState(),
        storageInfoData,
        free,
        freeFormatted: formatBytes(free),
        total,
        totalFormatted: formatBytes(total),
        items,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'general-settings-info-storage');

    return { generalSettingsInfoStorage: this.store(selectAll, shallow) };
  }
}

function selectAll(state: GeneralSettingsInfoStorageStoreState) {
  return {
    ...selectBase(state),
    storageInfoData: state.storageInfoData,
    free: state.free,
    freeFormatted: state.freeFormatted,
    total: state.total,
    totalFormatted: state.totalFormatted,
    items: state.items,
  };
}

export const generalSettingsInfoStorageStore = new GeneralSettingsInfoStorageStore();
